import { PATIENT_REGISTRATION_FILE_MAX_SIZE_MB } from "@/components/Patient/patientRegistrationUploadLimits";
import { FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import useFileManager from "@/hooks/useFileManager";
import { cn } from "@/lib/utils";
import { PatientFileRecord } from "@/types/emr/patient/patientMasterData";
import { FileCategory, FileReadMinimal, FileType } from "@/types/files/file";
import { X } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface PatientRegistrationFileFieldProps {
  label?: string;
  showLabel?: boolean;
  patientId?: string;
  value?: PatientFileRecord | null;
  pendingFile?: File | null;
  onChange: (value: {
    fileId?: string;
    fileRecord?: PatientFileRecord | null;
    pendingFile?: File | null;
  }) => void;
  allowDownload?: boolean;
  showViewDownload?: boolean;
  required?: boolean;
  allowedExtensions?: string[];
  maxSizeMB?: number;
  disabled?: boolean;
}

export const PatientRegistrationFileField = ({
  label,
  showLabel = true,
  patientId,
  value,
  pendingFile,
  onChange,
  allowDownload = false,
  showViewDownload = true,
  required = false,
  allowedExtensions = ["jpg", "jpeg", "png", "pdf"],
  maxSizeMB = PATIENT_REGISTRATION_FILE_MAX_SIZE_MB,
  disabled = false,
}: PatientRegistrationFileFieldProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const fileManager = useFileManager({ type: FileType.PATIENT });
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const hasFile = !!(pendingFile || value?.id);
  const displayName = pendingFile?.name || value?.name;
  const canDownload = allowDownload && !!patientId && !!value?.id;

  const validateFile = (file: File) => {
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(t("file_size_exceeds_limit", { size: maxSizeMB }));
      return false;
    }

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    if (!allowedExtensions.includes(extension)) {
      setError(
        t("file_error__file_type", {
          extension,
          allowedExtensions: allowedExtensions.join(", "),
        }),
      );
      return false;
    }

    setError(null);
    return true;
  };

  const handleSelect = async (file: File) => {
    if (!validateFile(file)) {
      return;
    }

    onChange({ pendingFile: file, fileRecord: null, fileId: undefined });
  };

  const handleClear = () => {
    onChange({ fileId: undefined, fileRecord: null, pendingFile: null });
    setError(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleView = () => {
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return;
    }

    if (value?.id && patientId) {
      const fileForActions: FileReadMinimal = {
        id: value.id,
        name: value.name,
        extension: value.extension,
        file_type: FileType.PATIENT,
        file_category: FileCategory.IDENTITY_PROOF,
        associating_id: patientId,
        mime_type: "",
        archived_by: null as never,
        archived_datetime: "",
        upload_completed: true,
        created_date: "",
        uploaded_by: null as never,
      };
      void fileManager.viewFile(fileForActions, patientId);
    }
  };

  const handleDownload = () => {
    if (!value?.id || !patientId) {
      return;
    }
    const fileForActions: FileReadMinimal = {
      id: value.id,
      name: value.name,
      extension: value.extension,
      file_type: FileType.PATIENT,
      file_category: FileCategory.IDENTITY_PROOF,
      associating_id: patientId,
      mime_type: "",
      archived_by: null as never,
      archived_datetime: "",
      upload_completed: true,
      created_date: "",
      uploaded_by: null as never,
    };
    void fileManager.downloadFile(fileForActions, patientId);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) {
      return;
    }
    const file = event.dataTransfer.files?.[0];
    if (file) {
      void handleSelect(file);
    }
  };

  const inlineActions =
    hasFile && showViewDownload ? (
      <span className="inline-flex shrink-0 items-center gap-1">
        <button
          type="button"
          className="rounded px-1 py-0.5 text-sm font-medium text-primary hover:bg-primary-50"
          onClick={handleView}
        >
          {t("view")}
        </button>
        {canDownload && (
          <button
            type="button"
            className="rounded px-1 py-0.5 text-sm font-medium text-primary hover:bg-primary-50"
            onClick={handleDownload}
          >
            {t("download")}
          </button>
        )}
      </span>
    ) : null;

  const clearAction = hasFile ? (
    <button
      type="button"
      className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
      aria-label={t("clear")}
      disabled={disabled}
      onClick={handleClear}
    >
      <X className="size-3.5" />
    </button>
  ) : null;

  return (
    <FormItem className={cn(!showLabel && "gap-1", "w-full min-w-0")}>
      {showLabel && label && (
        <FormLabel aria-required={required && !hasFile}>{label}</FormLabel>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={allowedExtensions.map((ext) => `.${ext}`).join(",")}
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            void handleSelect(file);
          }
          event.target.value = "";
        }}
      />
      <div
        className={cn(
          "flex h-9 w-full max-w-full min-w-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-xs transition-colors",
          isDragging && "border-primary bg-primary/5",
          disabled && "cursor-not-allowed opacity-60",
        )}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) {
            setIsDragging(true);
          }
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        {hasFile ? (
          <>
            <span
              title={displayName}
              className="min-w-0 flex-1 truncate text-gray-700 overflow-hidden whitespace-nowrap"
            >
              {displayName}
            </span>
            <div className="ml-auto flex shrink-0 items-center gap-1">
              {inlineActions}
              {clearAction}
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              className="shrink-0 font-medium text-primary hover:underline"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              {t("choose_file_inline")}
            </button>
            <span className="truncate text-gray-500">
              {t("no_file_chosen")}
            </span>
          </>
        )}
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <FormMessage />
      {fileManager.Dialogues}
    </FormItem>
  );
};
